// Manager proposal preview target: a full package or a single library page.

import {
  PROPOSAL_PAGE_ARCHETYPE_LABELS,
  PROPOSAL_PAGE_ARCHETYPES,
  type ProposalPage,
  type ProposalPageArchetype,
} from "./proposalPageTypes";
import type { ProposalAlternate } from "./proposalAlternateTypes";
import { proposalPackageSelectOptions } from "./proposalPrimaryPackage";
import type { OpportunityFormatKey } from "../../types/opportunity.js";
import type { OpportunityDocumentArchetype } from "./documentArchetypes";

export type PreviewSelection =
  | { kind: "package"; alternateKey: string }
  | { kind: "page"; pageId: string };

export type PreviewSelectOption =
  | { kind: "package"; value: string; label: string; alternateKey: string }
  | {
      kind: "page";
      value: string;
      label: string;
      pageId: string;
      archetype: ProposalPageArchetype;
    };

export function encodePreviewSelection(s: PreviewSelection): string {
  return s.kind === "package" ? `pkg:${s.alternateKey}` : `page:${s.pageId}`;
}

export function decodePreviewSelection(raw: string): PreviewSelection | null {
  const v = raw.trim();
  if (v.startsWith("pkg:")) {
    const alternateKey = v.slice(4).trim();
    return alternateKey ? { kind: "package", alternateKey } : null;
  }
  if (v.startsWith("page:")) {
    const pageId = v.slice(5).trim();
    return pageId ? { kind: "page", pageId } : null;
  }
  // Legacy: bare alternate key from older local state.
  if (v) return { kind: "package", alternateKey: v };
  return null;
}

export function buildPreviewSelectOptions(args: {
  alternates: readonly ProposalAlternate[];
  pages: readonly ProposalPage[];
  formatKey: OpportunityFormatKey;
  productType: string;
  archetype?: OpportunityDocumentArchetype;
}): PreviewSelectOption[] {
  const packages = proposalPackageSelectOptions({
    alternates: args.alternates,
    formatKey: args.formatKey,
    productType: args.productType,
    archetype: args.archetype,
  }).map(
    (a): PreviewSelectOption => ({
      kind: "package",
      value: encodePreviewSelection({ kind: "package", alternateKey: a.alternate_key }),
      label: a.label,
      alternateKey: a.alternate_key,
    }),
  );

  const pages: PreviewSelectOption[] = [];
  for (const archetype of PROPOSAL_PAGE_ARCHETYPES) {
    const group = args.pages
      .filter((p) => p.archetype === archetype)
      .slice()
      .sort((a, b) => a.sort_order - b.sort_order || a.label.localeCompare(b.label));
    for (const p of group) {
      pages.push({
        kind: "page",
        value: encodePreviewSelection({ kind: "page", pageId: p.id }),
        label: `${PROPOSAL_PAGE_ARCHETYPE_LABELS[archetype]} · ${p.label}`,
        pageId: p.id,
        archetype,
      });
    }
  }

  return [...packages, ...pages];
}

export function selectionStillValid(
  selection: PreviewSelection,
  options: readonly PreviewSelectOption[],
): boolean {
  const encoded = encodePreviewSelection(selection);
  return options.some((o) => o.value === encoded);
}
